import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { 
  Mail, Lock, User, Phone, Building, BadgeCheck, Search, 
  ArrowRight, CheckCircle, Users, GraduationCap, Share2, 
  Briefcase, Zap, Gift, KeyRound, X
} from 'lucide-react';
import { cn } from '../lib/utils';

const LOGO_URL = "/images/area-logo.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_OPTIONS = [
  "Acquisition Analyst", "Administrative Assistant", "AI / Automation Specialist (Real Estate)",
  "Appraiser", "Architect", "Asset Manager", "RE Associate Broker",
  "RE Broker (Managing Broker / Principal Broker)", "Builder / General Contractor",
  "Building Inspector (Municipal)", "Civil Engineer", "Cleaner / Janitorial",
  "Client Care Coordinator", "Closing Agent / Settlement Agent",
  "Commercial Agent/Salesperson", "Commercial Broker", "Commercial Inspector",
  "Commercial Loan Officer", "Compliance Auditor", "Compliance Officer",
  "Construction Project Manager", "CRE Analyst", "Credit Analyst",
  "Director of Operations", "Disposition Specialist", "Drone Operator",
  "Electrician", "Energy Auditor", "Environmental Inspector",
  "Escrow Assistant", "Escrow Officer", "Facilities Manager",
  "Feasibility Analyst", "Fractional Ownership Manager", "Fund Manager (Real Estate Funds)",
  "General Contractor", "Hard Money Lender", "Home Inspector",
  "Housing Authority Specialist", "HVAC Technician", "Industrial Specialist",
  "Inside Sales Agent (ISA)", "Investment Advisor", "Investment Sales Broker",
  "Land Planner", "Landlord", "Landscaper", "Lead Generation Specialist",
  "Leasing Agent", "Leasing Broker", "Listing Coordinator",
  "Maintenance Coordinator", "Market Analyst", "Marketing Director",
  "Mortgage Broker", "Mortgage Loan Officer", "Mover", "Notary Public",
  "Office Manager", "Pest Inspector", "Photographer", "Plumber",
  "Portfolio Manager", "Private Money Lender", "Probate Specialist",
  "Property Manager (Commercial / Residential)", "PropTech Product Manager",
  "RE Agent/Salesperson", "Real Estate Attorney", "Real Estate Consultant",
  "Real Estate Developer", "Real Estate Investor", "Recruiter",
  "Relocation Specialist", "Roofer", "SEO Specialist", "Settlement Processor",
  "STR/Short Term Rental Manager (Airbnb Manager)", "Showing Agent",
  "Site Selection Specialist", "Social Media Manager", "Stager",
  "Structural Engineer", "Subcontractor", "Surveyor", "Syndicator",
  "Tax Assessor", "Team Leader", "Tenant Rep Broker", "Title Agent",
  "Title Examiner", "Title Officer", "Training/Coaching",
  "Transaction Coordinator", "Underwriter", "Urban Planner",
  "Valuation Consultant", "Videographer", "Website Developer", "Zoning Officer"
];

const GEOGRAPHIC_AREA_OPTIONS = [
  { value: "20_miles", label: "20 Miles" },
  { value: "50_miles", label: "50 Miles" },
  { value: "100_miles", label: "100 Miles" },
  { value: "entire_state", label: "Entire State" }
];

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming"
];

const allianceBenefits = [
  { icon: Users, text: "Private member community" },
  { icon: Share2, text: "Referral opportunities" },
  { icon: GraduationCap, text: "Education and courses" },
  { icon: Briefcase, text: "Job board access" },
  { icon: Gift, text: "Partner discounts" },
  { icon: Zap, text: "Moxey OS discounts" }
];

const licenseRequiredRoles = [
  'RE Agent/Salesperson', 'RE Associate Broker',
  'RE Broker (Managing Broker / Principal Broker)', 'Commercial Agent/Salesperson',
  'Commercial Broker', 'Leasing Agent', 'Leasing Broker',
  'Investment Sales Broker', 'Tenant Rep Broker', 'Showing Agent'
];

function StateMultiSelect({ selected, onToggle }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = search
    ? US_STATES.filter(s => s.toLowerCase().includes(search.toLowerCase()))
    : US_STATES;

  return (
    <div className="relative" ref={ref} data-testid="register-license-states">
      {/* Selected tags */}
      <div
        className="min-h-[44px] border rounded-lg bg-white px-2 py-1.5 flex flex-wrap gap-1.5 items-center cursor-text"
        onClick={() => setOpen(true)}
      >
        {selected.map(s => (
          <span
            key={s}
            className="inline-flex items-center gap-1 bg-[#3D4F7A] text-white text-xs font-medium px-2.5 py-1 rounded-md hover:bg-[#2A3755] transition-colors cursor-pointer"
            onClick={(e) => { e.stopPropagation(); onToggle(s); }}
            data-testid={`state-tag-${s.replace(/\s+/g, '-').toLowerCase()}`}
          >
            {s}
            <X className="w-3 h-3" />
          </span>
        ))}
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={selected.length === 0 ? 'Search states...' : ''}
          className="flex-1 min-w-[100px] text-sm outline-none bg-transparent h-7 placeholder:text-slate-400"
          data-testid="state-search-input"
        />
      </div>
      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-500">No states match</div>
          ) : (
            filtered.map(st => {
              const isSelected = selected.includes(st);
              return (
                <div
                  key={st}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 cursor-pointer text-sm transition-colors',
                    isSelected ? 'bg-[#3D4F7A]/10 text-[#3D4F7A] font-medium' : 'hover:bg-slate-50'
                  )}
                  onClick={() => { onToggle(st); setSearch(''); }}
                  data-testid={`state-option-${st.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  <div className={cn(
                    'w-4 h-4 rounded border flex items-center justify-center flex-shrink-0',
                    isSelected ? 'bg-[#3D4F7A] border-[#3D4F7A]' : 'border-slate-300'
                  )}>
                    {isSelected && <CheckCircle className="w-3 h-3 text-white" />}
                  </div>
                  {st}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}

export default function Register() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { updateUser } = useAuth();
  const roleDropdownRef = useRef(null);
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [roleOpen, setRoleOpen] = useState(false);
  const [roleSearch, setRoleSearch] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationToken, setVerificationToken] = useState(null);
  const [codeSent, setCodeSent] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', user_role: '', email: '', phone: '',
    password: '', confirmPassword: '', license_number: '', license_states: [],
    brokerage_name: '', brokerage_city: '', brokerage_state: '',
    geographic_area: '20_miles', membership_tier: ''
  });

  const [statesOpen, setStatesOpen] = useState(false);
  const [roleOptions, setRoleOptions] = useState(ROLE_OPTIONS);
  const [membershipTiers, setMembershipTiers] = useState([]);

  const requiresLicense = licenseRequiredRoles.includes(formData.user_role);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target)) {
        setRoleOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const cancelled = searchParams.get('cancelled');
    if (cancelled) { toast.error('Payment was cancelled. Please try again.'); return; }
    if (sessionId) { verifyPaymentAndComplete(sessionId); }
  }, [searchParams]);

  useEffect(() => { fetchMembershipTiers(); fetchRoles(); }, []);

  const fetchRoles = async () => {
    try {
      const response = await fetch(`${API}/roles`);
      if (response.ok) {
        const data = await response.json();
        if (data.roles && data.roles.length > 0) setRoleOptions(data.roles);
      }
    } catch (error) { console.error('Error fetching roles:', error); }
  };

  const fetchMembershipTiers = async () => {
    try {
      const response = await fetch(`${API}/membership/tiers`);
      if (response.ok) {
        const data = await response.json();
        setMembershipTiers(data.tiers);
      }
    } catch (error) { console.error('Error fetching tiers:', error); }
  };

  const verifyPaymentAndComplete = async (sessionId) => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/payment/status/${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.payment_status === 'paid') {
          if (data.registration_data) {
            await completeRegistration(data.registration_data, sessionId);
          } else {
            toast.success('Payment successful! Please complete your registration.');
            setStep(1);
          }
        } else { toast.error('Payment not completed. Please try again.'); }
      }
    } catch (error) { toast.error('Error verifying payment'); }
    finally { setLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleState = (state) => {
    setFormData(prev => {
      const states = prev.license_states.includes(state)
        ? prev.license_states.filter(s => s !== state)
        : [...prev.license_states, state];
      return { ...prev, license_states: states };
    });
  };

  const validateStep1 = () => {
    if (!formData.first_name.trim()) { toast.error('First name is required'); return false; }
    if (!formData.last_name.trim()) { toast.error('Last name is required'); return false; }
    if (!formData.user_role) { toast.error('Please select your role'); return false; }
    if (!formData.email.trim()) { toast.error('Email is required'); return false; }
    if (!formData.phone.trim()) { toast.error('Phone number is required'); return false; }
    if (!formData.password || formData.password.length < 8) { toast.error('Password must be at least 8 characters'); return false; }
    if (formData.password !== formData.confirmPassword) { toast.error('Passwords do not match'); return false; }
    if (requiresLicense) {
      if (!formData.license_number.trim()) { toast.error('RE License # is required for your role'); return false; }
      if (formData.license_states.length === 0) { toast.error('At least one State Licensed is required'); return false; }
    }
    if (!formData.brokerage_name.trim()) { toast.error('Business name is required'); return false; }
    if (!formData.brokerage_city.trim()) { toast.error('City is required'); return false; }
    if (!formData.brokerage_state) { toast.error('State is required'); return false; }
    return true;
  };

  const handleStep1Submit = async (e) => {
    e.preventDefault();
    if (!validateStep1()) return;
    // Send verification code
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/send-verification-code`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });
      if (res.ok) {
        toast.success('Verification code sent to your email!');
        setCodeSent(true);
        setResendTimer(60);
        setStep(2);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to send verification code');
      }
    } catch (error) {
      toast.error('Error sending verification code');
    }
    setLoading(false);
  };

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const resendCode = async () => {
    if (resendTimer > 0) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/send-verification-code`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email })
      });
      if (res.ok) {
        toast.success('New code sent!');
        setResendTimer(60);
        setVerificationCode('');
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to resend code');
      }
    } catch (error) {
      toast.error('Error resending code');
    }
    setLoading(false);
  };

  const handleVerifyCode = async () => {
    if (verificationCode.length !== 6) { toast.error('Please enter the 6-digit code'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/auth/verify-code`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email, code: verificationCode })
      });
      if (res.ok) {
        const data = await res.json();
        setVerificationToken(data.verification_token);
        toast.success('Email verified!');
        setStep(3);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Invalid verification code');
      }
    } catch (error) {
      toast.error('Error verifying code');
    }
    setLoading(false);
  };

  const handleMembershipSelect = async (tier) => {
    setFormData(prev => ({ ...prev, membership_tier: tier.id }));
    if (tier.id === 'basic') {
      await completeRegistration({ ...formData, membership_tier: 'basic' }, null);
    } else {
      setLoading(true);
      try {
        const response = await fetch(`${API}/payment/create-checkout`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            membership_tier: tier.id, origin_url: window.location.origin,
            user_email: formData.email,
            registration_data: { ...formData, membership_tier: tier.id }
          })
        });
        if (response.ok) {
          const data = await response.json();
          window.location.href = data.checkout_url;
        } else {
          const error = await response.json();
          toast.error(error.detail || 'Error creating payment session');
        }
      } catch (error) { toast.error('Error processing payment'); }
      finally { setLoading(false); }
    }
  };

  const completeRegistration = async (data, sessionId) => {
    setLoading(true);
    try {
      const url = sessionId 
        ? `${API}/auth/register-with-payment?session_id=${sessionId}`
        : `${API}/auth/register-with-payment`;
      const response = await fetch(url, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: data.email, password: data.password,
          first_name: data.first_name, last_name: data.last_name,
          phone: data.phone, user_role: data.user_role,
          license_number: data.license_number || null,
          license_states: data.license_states || [],
          brokerage_name: data.brokerage_name, brokerage_city: data.brokerage_city,
          brokerage_state: data.brokerage_state,
          geographic_area: data.geographic_area || '20_miles',
          membership_tier: data.membership_tier
        })
      });
      if (response.ok) {
        const result = await response.json();
        document.cookie = `session_token=${result.session_token}; path=/; max-age=${7 * 24 * 60 * 60}`;
        // Set user directly from registration response — avoids async checkAuth race condition
        updateUser(result.user);
        toast.success('Registration successful! Welcome to AREA.');
        navigate('/dashboard');
        return;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Registration failed');
      }
    } catch (error) {
      toast.error('Registration failed');
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3D4F7A] mx-auto mb-4"></div>
          <p className="text-slate-600">Processing...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="register-page">
      {/* Left Panel - Alliance Benefits (desktop only) */}
      <div className="hidden lg:flex lg:w-[420px] xl:w-[480px] bg-[#3D4F7A] relative flex-col justify-between p-10 text-white flex-shrink-0">
        <div>
          <Link to="/">
            <img src={LOGO_URL} alt="AREA" className="h-12 w-auto mb-10 brightness-0 invert" />
          </Link>
          <h2 className="text-3xl font-bold leading-tight mb-4">
            Join the alliance
          </h2>
          <p className="text-slate-300 leading-relaxed mb-10">
            Become part of a growing network of real estate professionals committed to 
            collaboration, growth, and shared success.
          </p>
          <div className="space-y-4">
            {allianceBenefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-5 h-5 text-white" />
                </div>
                <span className="text-slate-200 font-medium">{benefit.text}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="pt-8 border-t border-white/10">
          <p className="text-sm text-slate-400">
            Basic membership is <span className="text-white font-semibold">FREE</span>. 
            Upgrade anytime to unlock premium features.
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 overflow-y-auto">
        <form autoComplete="off" className="w-full max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <Link to="/">
              <img src={LOGO_URL} alt="AREA" className="h-14 w-auto mx-auto mb-3" />
            </Link>
          </div>

          {/* Header */}
          <div className="mb-8">
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900">
              {step === 1 ? 'Create your account' : step === 2 ? 'Verify your email' : 'Choose your membership'}
            </h1>
            <p className="text-slate-500 mt-1">
              {step === 1 
                ? 'Fill in your details to join the American Real Estate Alliance' 
                : step === 2 
                ? `Enter the 6-digit code sent to ${formData.email}`
                : 'Choose the membership that works best for you'}
            </p>
          </div>

          {/* Progress */}
          <div className="flex items-center gap-3 mb-8">
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= 1 ? 'bg-[#3D4F7A] text-white' : 'bg-slate-200 text-slate-500'}`}>1</div>
              <span className="text-sm font-medium text-slate-700 hidden sm:inline">Your Details</span>
            </div>
            <div className={`flex-1 h-0.5 ${step >= 2 ? 'bg-[#3D4F7A]' : 'bg-slate-200'}`}></div>
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= 2 ? 'bg-[#3D4F7A] text-white' : 'bg-slate-200 text-slate-500'}`}>2</div>
              <span className="text-sm font-medium text-slate-700 hidden sm:inline">Verify Email</span>
            </div>
            <div className={`flex-1 h-0.5 ${step >= 3 ? 'bg-[#3D4F7A]' : 'bg-slate-200'}`}></div>
            <div className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${step >= 3 ? 'bg-[#3D4F7A] text-white' : 'bg-slate-200 text-slate-500'}`}>3</div>
              <span className="text-sm font-medium text-slate-700 hidden sm:inline">Membership</span>
            </div>
          </div>

          {step === 1 && (
            <Card className="border border-slate-200 shadow-lg">
              <CardContent className="p-6 sm:p-8">
                <form onSubmit={handleStep1Submit} className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-1 flex items-center gap-2">
                      <User className="w-5 h-5 text-[#3D4F7A]" />
                      Personal Information
                    </h3>
                    <p className="text-xs text-slate-500 mb-4">Fields marked with * are required</p>
                    
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="first_name">First Name *</Label>
                          <Input id="first_name" name="first_name" value={formData.first_name} onChange={handleChange} placeholder="John" required className="h-11" autoComplete="off" data-testid="register-first-name" />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="last_name">Last Name *</Label>
                          <Input id="last_name" name="last_name" value={formData.last_name} onChange={handleChange} placeholder="Doe" required className="h-11" autoComplete="off" data-testid="register-last-name" />
                        </div>
                      </div>

                      <div className="space-y-2" ref={roleDropdownRef}>
                        <Label>Your Role *</Label>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                          <Input
                            type="text"
                            value={roleSearch || formData.user_role}
                            onChange={(e) => { setRoleSearch(e.target.value); if (e.target.value !== formData.user_role) setFormData(prev => ({ ...prev, user_role: '' })); setRoleOpen(true); }}
                            onFocus={() => setRoleOpen(true)}
                            placeholder="Begin Typing"
                            className="h-11 pl-9 placeholder:text-slate-400"
                            data-testid="register-role"
                          />
                          {roleOpen && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
                              {roleOptions.filter(role => {
                                const search = (roleSearch || '').toLowerCase();
                                if (!search) return true;
                                const roleLower = role.toLowerCase();
                                if (roleLower.includes(search)) return true;
                                const roleExpanded = roleLower.replace(/^re /, 'real estate ');
                                return roleExpanded.includes(search);
                              }).length === 0 ? (
                                <div className="px-3 py-2 text-sm text-slate-500">No matching roles found</div>
                              ) : (
                                roleOptions.filter(role => {
                                  const search = (roleSearch || '').toLowerCase();
                                  if (!search) return true;
                                  const roleLower = role.toLowerCase();
                                  if (roleLower.includes(search)) return true;
                                  const roleExpanded = roleLower.replace(/^re /, 'real estate ');
                                  return roleExpanded.includes(search);
                                }).map(role => (
                                  <div key={role} className={`px-3 py-2 cursor-pointer hover:bg-slate-100 text-sm ${formData.user_role === role ? 'bg-[#3D4F7A]/10 text-[#3D4F7A] font-medium' : ''}`}
                                    onClick={() => { setFormData(prev => ({ ...prev, user_role: role })); setRoleSearch(''); setRoleOpen(false); }}>
                                    {role}
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address *</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="you@example.com" required className="pl-9 h-11" autoComplete="off" data-testid="register-email" />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone *</Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="(555) 123-4567" required className="pl-9 h-11" autoComplete="off" data-testid="register-phone" />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="password">Password *</Label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input id="password" name="password" type="password" value={formData.password} onChange={handleChange} placeholder="Min 8 characters" required className="pl-9 h-11" autoComplete="new-password" data-testid="register-password" />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="confirmPassword">Confirm Password *</Label>
                          <div className="relative">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input id="confirmPassword" name="confirmPassword" type="password" value={formData.confirmPassword} onChange={handleChange} placeholder="Confirm password" required className="pl-9 h-11" autoComplete="new-password" data-testid="register-confirm-password" />
                          </div>
                        </div>
                      </div>

                      {requiresLicense && (
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-4">
                          <div className="space-y-2">
                            <Label htmlFor="license_number">RE License # *</Label>
                            <Input id="license_number" name="license_number" value={formData.license_number} onChange={handleChange} placeholder="License number" required={requiresLicense} className="h-11" autoComplete="off" data-testid="register-license" />
                          </div>
                          <div className="space-y-2">
                            <Label>State(s) Licensed *</Label>
                            <StateMultiSelect
                              selected={formData.license_states}
                              onToggle={toggleState}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="pt-4 border-t">
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Building className="w-5 h-5 text-[#3D4F7A]" />
                      Business Information
                    </h3>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="brokerage_name">Business Name *</Label>
                        <Input id="brokerage_name" name="brokerage_name" value={formData.brokerage_name} onChange={handleChange} placeholder="ABC Realty" required className="h-11" autoComplete="off" data-testid="register-brokerage-name" />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="brokerage_city">City *</Label>
                          <Input id="brokerage_city" name="brokerage_city" value={formData.brokerage_city} onChange={handleChange} placeholder="City" required className="h-11" autoComplete="off" data-testid="register-brokerage-city" />
                        </div>
                        <div className="space-y-2">
                          <Label>State *</Label>
                          <Select value={formData.brokerage_state} onValueChange={(value) => setFormData(prev => ({ ...prev, brokerage_state: value }))}>
                            <SelectTrigger className="h-11" data-testid="register-brokerage-state"><SelectValue placeholder="Select state" /></SelectTrigger>
                            <SelectContent>{US_STATES.map(state => (<SelectItem key={state} value={state}>{state}</SelectItem>))}</SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Geographic Area Covered</Label>
                        <Select value={formData.geographic_area} onValueChange={(value) => setFormData(prev => ({ ...prev, geographic_area: value }))}>
                          <SelectTrigger className="h-11" data-testid="register-geographic-area"><SelectValue placeholder="Select coverage area" /></SelectTrigger>
                          <SelectContent>{GEOGRAPHIC_AREA_OPTIONS.map(option => (<SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>))}</SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500">Based on your business address</p>
                      </div>
                    </div>
                  </div>

                  <Button type="submit" className="w-full h-12 bg-[#3D4F7A] hover:bg-[#2A3755] text-base font-semibold" data-testid="register-continue">
                    Continue to Membership
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </form>

                <p className="text-center text-sm text-slate-600 mt-6">
                  Already have an account?{' '}
                  <Link to="/login" className="text-[#3D4F7A] font-semibold hover:underline">Sign in</Link>
                </p>
              </CardContent>
            </Card>
          )}

          {step === 2 && (
            <Card className="border border-slate-200 shadow-lg" data-testid="verify-email-step">
              <CardContent className="p-6 sm:p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-6">
                  <Mail className="w-8 h-8 text-[#3D4F7A]" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Check your inbox</h3>
                <p className="text-slate-500 mb-8">
                  We sent a 6-digit verification code to <span className="font-semibold text-slate-700">{formData.email}</span>
                </p>
                <div className="flex justify-center gap-2 mb-6">
                  <Input
                    type="text"
                    maxLength={6}
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    className="w-48 h-14 text-center text-2xl font-bold tracking-[0.3em] border-2 border-slate-300 focus:border-[#3D4F7A]"
                    data-testid="verification-code-input"
                    onKeyDown={(e) => { if (e.key === 'Enter') handleVerifyCode(); }}
                  />
                </div>
                <Button 
                  onClick={handleVerifyCode} 
                  disabled={verificationCode.length !== 6 || loading}
                  className="w-full max-w-xs h-12 bg-[#3D4F7A] hover:bg-[#2A3755] text-base font-semibold mx-auto mb-4"
                  data-testid="verify-code-submit"
                >
                  {loading ? 'Verifying...' : 'Verify Email'}
                  <CheckCircle className="ml-2 w-5 h-5" />
                </Button>
                <p className="text-sm text-slate-500">
                  Didn't receive the code?{' '}
                  {resendTimer > 0 ? (
                    <span className="text-slate-400">Resend in {resendTimer}s</span>
                  ) : (
                    <button onClick={resendCode} className="text-[#3D4F7A] font-semibold hover:underline" data-testid="resend-code-btn">
                      Resend Code
                    </button>
                  )}
                </p>
                <div className="mt-6">
                  <Button variant="ghost" onClick={() => setStep(1)} className="text-slate-600">Back to Registration</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {membershipTiers.map((tier) => (
                  <Card key={tier.id} className={cn("border-2 transition-all cursor-pointer hover:shadow-lg", tier.id === 'pro' ? 'border-[#3D4F7A] shadow-lg' : 'border-slate-200')}
                    onClick={() => handleMembershipSelect(tier)} data-testid={`membership-tier-${tier.id}`}>
                    <CardContent className="p-6">
                      {tier.id === 'pro' && (
                        <div className="text-center mb-4"><span className="bg-[#3D4F7A] text-white text-xs font-semibold px-3 py-1 rounded-full">MOST POPULAR</span></div>
                      )}
                      {tier.id === 'founder' && (
                        <div className="text-center mb-4"><span className="bg-[#D42B3C] text-white text-xs font-semibold px-3 py-1 rounded-full">BEST VALUE</span></div>
                      )}
                      <div className="text-center mb-6">
                        <h3 className="font-heading text-xl font-bold text-slate-900">{tier.name}</h3>
                        <div className="mt-2">
                          <span className="text-4xl font-bold text-slate-900">${tier.price}</span>
                          {tier.interval && <span className="text-slate-600">/{tier.interval === 'year' ? 'year' : 'lifetime'}</span>}
                        </div>
                        {tier.id === 'basic' && <p className="text-green-600 font-semibold mt-1">FREE</p>}
                      </div>
                      <ul className="space-y-3 mb-6">
                        {tier.features?.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-slate-600">
                            <BadgeCheck className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />{feature}
                          </li>
                        ))}
                      </ul>
                      <Button className={cn("w-full", tier.id === 'pro' ? 'bg-[#3D4F7A] hover:bg-[#2A3755]' : tier.id === 'founder' ? 'bg-[#D42B3C] hover:bg-[#a82230]' : 'bg-slate-600 hover:bg-slate-700')}
                        onClick={(e) => { e.stopPropagation(); handleMembershipSelect(tier); }}
                        data-testid={`membership-select-${tier.id}`}
                      >
                        {tier.id === 'basic' ? 'Get Started Free' : `Select ${tier.name}`}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <div className="text-center">
                <Button variant="ghost" onClick={() => setStep(1)} className="text-slate-600">Back to Registration</Button>
              </div>
            </div>
          )}

          <p className="text-center text-xs text-slate-500 mt-8">
            By joining, you agree to our{' '}
            <Link to="/terms" className="underline">Terms of Service</Link>{' '}and{' '}
            <Link to="/privacy" className="underline">Privacy Policy</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
